import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  X, ChevronRight, Sun, Moon, Download, Send, Rss,
  Newspaper, TrendingUp, Wallet, Activity, Globe, HeartPulse, 
  Microscope, Cpu, Crown, User, LogOut, Sparkles, Trophy
} from "lucide-react";
import { BACKEND_URL } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useTheme } from "../context/ThemeContext";
import { useMember } from "../context/MemberContext";

const categories = [
  { name: 'Gündem', icon: Newspaper, color: 'text-red-400', bg: 'bg-red-500/10' },
  { name: 'Ekonomi', icon: Wallet, color: 'text-green-400', bg: 'bg-green-500/10' },
  { name: 'Spor', icon: Activity, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  { name: 'Teknoloji', icon: Cpu, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  { name: 'Dünya', icon: Globe, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  { name: 'Sağlık', icon: HeartPulse, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  { name: 'Bilim', icon: Microscope, color: 'text-amber-400', bg: 'bg-amber-500/10' },
];

const MobileMenu = ({ isOpen, onClose, installPrompt, onInstall }) => {
  const navigate = useNavigate();
  const { lang, setLang, LANGS } = useLang();
  const { theme, toggleTheme } = useTheme();
  const { member, logout } = useMember();
  const [showLangs, setShowLangs] = useState(false);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleCategoryClick = (category) => {
    onClose();
    navigate(`/?category=${encodeURIComponent(category)}`);
  };

  const handleLogout = () => {
    logout();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-menu">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm animate-fadeIn"
        onClick={onClose}
      />

      {/* Menu Panel */}
      <div className="absolute right-0 top-0 bottom-0 w-[85%] max-w-sm bg-[#0a0a0b] border-l border-zinc-800/50 animate-slideInRight overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0a0a0b]/95 backdrop-blur-xl border-b border-zinc-800/50 px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-amber-400 rounded-xl flex items-center justify-center">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="18" rx="1.5" />
                  <rect x="8" y="3" width="13" height="18" rx="1.5" />
                </svg>
              </div>
              <div>
                <p className="text-lg font-bold text-white">loft<span className="text-amber-500">.press</span></p>
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Menü</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-xl transition-colors"
              data-testid="mobile-menu-close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* User Section */}
        {member ? (
          <div className="px-4 py-4 border-b border-zinc-800/50">
            <div className="flex items-center gap-3 mb-3">
              {member.picture ? (
                <img src={member.picture} alt="" className="w-12 h-12 rounded-xl object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                  <User className="w-6 h-6 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold truncate">{member.name}</p>
                <p className="text-xs text-zinc-500 truncate">{member.email}</p>
              </div>
              {member.role === "premium" && (
                <div className="flex items-center gap-1 px-2 py-1 bg-amber-500/10 rounded-full">
                  <Crown className="w-3.5 h-3.5 text-amber-500" />
                  <span className="text-[10px] text-amber-500 font-bold">PRO</span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Link 
                to="/profil" 
                onClick={onClose}
                className="flex items-center justify-center gap-2 py-2.5 bg-zinc-800/80 hover:bg-zinc-700 text-white text-sm rounded-xl transition-colors"
              >
                <User className="w-4 h-4" /> Profilim
              </Link>
              <button 
                onClick={handleLogout}
                className="flex items-center justify-center gap-2 py-2.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm rounded-xl transition-colors"
              >
                <LogOut className="w-4 h-4" /> Çıkış
              </button>
            </div>
            {member.role !== "premium" && (
              <Link 
                to="/premium" 
                onClick={onClose}
                className="mt-3 flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold text-sm rounded-xl"
              >
                <Crown className="w-4 h-4" /> Premium'a Geç
              </Link>
            )}
          </div>
        ) : (
          <div className="px-4 py-4 border-b border-zinc-800/50">
            <Link 
              to="/uye" 
              onClick={onClose}
              className="flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold text-sm rounded-xl"
              data-testid="mobile-menu-login"
            >
              <User className="w-4 h-4" /> Giriş Yap / Kayıt Ol
            </Link>
          </div>
        )}

        {/* AI Assistant Quick Access */}
        <div className="px-4 py-4 border-b border-zinc-800/50">
          <Link 
            to="/ai-asistan" 
            onClick={onClose}
            className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-2xl group mb-3"
            data-testid="mobile-menu-ai"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-white font-semibold group-hover:text-blue-400 transition-colors">AI Haber Asistanı</p>
              <p className="text-xs text-zinc-500">Haberler hakkında sorular sorun</p>
            </div>
            <ChevronRight className="w-5 h-5 text-zinc-600 group-hover:text-blue-400 transition-colors" />
          </Link>
          
          {/* Leaderboard Link */}
          <Link 
            to="/liderlik" 
            onClick={onClose}
            className="flex items-center gap-4 p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-2xl group"
            data-testid="mobile-menu-leaderboard"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Trophy className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-white font-semibold group-hover:text-amber-400 transition-colors">Liderlik Tablosu</p>
              <p className="text-xs text-zinc-500">Yazarlık yarışmasına katıl</p>
            </div>
            <ChevronRight className="w-5 h-5 text-zinc-600 group-hover:text-amber-400 transition-colors" />
          </Link>
        </div>

        {/* Categories */}
        <div className="px-4 py-4 border-b border-zinc-800/50">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-3 px-1">Kategoriler</p>
          <div className="grid grid-cols-2 gap-2">
            {categories.map((cat) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.name}
                  onClick={() => handleCategoryClick(cat.name)}
                  className={`flex items-center gap-3 p-3 ${cat.bg} rounded-xl transition-all active:scale-95`}
                  data-testid={`mobile-menu-cat-${cat.name.toLowerCase()}`}
                >
                  <Icon className={`w-5 h-5 ${cat.color}`} />
                  <span className="text-sm text-white font-medium">{cat.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Settings */}
        <div className="px-4 py-4 border-b border-zinc-800/50">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-3 px-1">Ayarlar</p>
          
          {/* Theme Toggle */}
          <button 
            onClick={toggleTheme}
            className="w-full flex items-center justify-between p-3 bg-zinc-900/50 rounded-xl mb-2"
            data-testid="mobile-menu-theme"
          >
            <div className="flex items-center gap-3">
              {theme === 'dark' ? <Moon className="w-5 h-5 text-blue-400" /> : <Sun className="w-5 h-5 text-amber-400" />}
              <span className="text-sm text-white">Tema</span>
            </div>
            <div className={`w-12 h-6 rounded-full p-1 transition-colors ${theme === 'dark' ? 'bg-blue-500/20' : 'bg-amber-500/20'}`}>
              <div className={`w-4 h-4 rounded-full transition-transform ${theme === 'dark' ? 'bg-blue-400 translate-x-6' : 'bg-amber-400 translate-x-0'}`} />
            </div>
          </button>

          {/* Language */}
          <div className="bg-zinc-900/50 rounded-xl overflow-hidden">
            <button 
              onClick={() => setShowLangs(!showLangs)}
              className="w-full flex items-center justify-between p-3"
              data-testid="mobile-menu-lang"
            >
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-purple-400" />
                <span className="text-sm text-white">Dil</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-zinc-400">{LANGS.find(l => l.code === lang)?.flag} {lang.toUpperCase()}</span>
                <ChevronRight className={`w-4 h-4 text-zinc-600 transition-transform ${showLangs ? 'rotate-90' : ''}`} />
              </div>
            </button>
            {showLangs && (
              <div className="px-3 pb-3 grid grid-cols-4 gap-2">
                {LANGS.map(l => (
                  <button
                    key={l.code}
                    onClick={() => { setLang(l.code); setShowLangs(false); }}
                    className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-colors ${
                      lang === l.code ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-800/50 text-zinc-400'
                    }`}
                  >
                    <span className="text-lg">{l.flag}</span>
                    <span className="text-[10px] font-medium">{l.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick Links */}
        <div className="px-4 py-4 border-b border-zinc-800/50">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-3 px-1">Kurumsal</p>
          <div className="space-y-1">
            {[
              { name: 'Hakkımızda', href: '/hakkimizda' },
              { name: 'Künye', href: '/kunye' },
              { name: 'İletişim', href: '/iletisim' },
            ].map(link => (
              <Link
                key={link.name}
                to={link.href}
                onClick={onClose}
                className="flex items-center justify-between p-3 text-zinc-400 hover:text-white hover:bg-zinc-800/50 rounded-xl transition-colors"
              >
                <span className="text-sm">{link.name}</span>
                <ChevronRight className="w-4 h-4" />
              </Link>
            ))}
          </div>
        </div>

        {/* Social & Install */}
        <div className="px-4 py-4">
          <div className="flex items-center gap-3">
            <a 
              href="https://t.me/loftpress" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded-xl transition-colors"
            >
              <Send className="w-4 h-4" />
              <span className="text-sm font-medium">Telegram</span>
            </a>
            <a 
              href={`${BACKEND_URL}/api/rss`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 rounded-xl transition-colors"
            >
              <Rss className="w-4 h-4" />
              <span className="text-sm font-medium">RSS</span>
            </a>
          </div>
          
          {installPrompt && (
            <button
              onClick={() => { onInstall(); onClose(); }}
              className="mt-3 w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold text-sm rounded-xl"
              data-testid="mobile-menu-install"
            >
              <Download className="w-4 h-4" />
              Uygulamayı Yükle
            </button>
          )}
        </div>

        {/* Footer padding for safe area */}
        <div className="h-20" />
      </div>
    </div>
  );
};

export default MobileMenu;
